import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { ReviewStrip } from "@/components/review-strip";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { FIRM, PENINSULA_ADDRESS, faqSchema } from "@/lib/firm";

const TITLE = "Commercial Conveyancing Lawyers Queensland | Coastside Law";
const DESCRIPTION =
  "Commercial property conveyancing across Queensland — GST, leases, due diligence and settlement. Solicitor-led, fixed fee quoted before we start.";

const FAQS = [
  {
    q: "What is the commercial conveyancing process?",
    a: "In order: review the contract before you sign (especially GST, special conditions and the due diligence period), carry out searches and read the leases, satisfy or end the finance and due diligence conditions by their dates, then prepare settlement figures with adjustments for rates, outgoings and rent, and settle electronically. Because there is no cooling-off period, the first step matters most.",
  },
  {
    q: "What is commercial conveyancing?",
    a: "It is the legal work involved in buying or selling commercial property — offices, retail shops, warehouses, industrial sheds, medical suites and commercial units. The mechanics look similar to a house sale, but the risk sits in different places: GST treatment, existing tenants and leases, permitted use under the planning scheme, outgoings, and environmental and contamination issues.",
  },
  {
    q: "How is it different from residential conveyancing?",
    a: "Three things mainly. First, there is no statutory cooling-off period for commercial property in Queensland, so the contract binds you from signature. Second, GST applies and how the contract handles it — margin scheme, going concern, or plus GST — can move the price by tens of thousands. Third, if the property is tenanted you are buying the leases as well as the land, and those leases need to be read before you sign, not after.",
  },
  {
    q: "Is GST payable on a commercial property sale?",
    a: "Usually yes, unless the sale qualifies as a going concern. A sale of a tenanted commercial property can be GST-free as the supply of a going concern where the parties agree in writing before settlement, the buyer is registered for GST, and everything necessary for continued operation is supplied. The clause has to be in the contract and the conditions genuinely met — you cannot fix it afterwards. Get it addressed before you sign, and take your accountant's advice alongside ours.",
  },
  {
    q: "Do I get a cooling-off period on commercial property?",
    a: "No. The statutory cooling-off period under the Property Occupations Act applies to residential property, not commercial. Once you sign a commercial contract you are bound, subject only to the conditions written into it. That is precisely why the contract review has to happen before signing.",
  },
  {
    q: "What due diligence should a commercial buyer do?",
    a: "Title and encumbrance searches, the leases and any guarantees or bank guarantees, the outgoings history and recovery provisions, council rates and water, planning and permitted use, building certification and any fire safety or disability compliance issues, contaminated land and environmental management register searches, and the plant and equipment or fit-out being included. For a strata unit, add the body corporate records, sinking fund and any levies or litigation on foot.",
  },
  {
    q: "What does commercial conveyancing cost?",
    a: `We quote a fixed fee once we have seen the contract or the terms of the deal, so you know the figure before you engage us. Commercial matters vary too much for a single advertised price — a vacant industrial shed and a multi-tenanted retail strip are different jobs. Call ${FIRM.phone} and we will scope it on the phone.`,
  },
  {
    q: "Can you act on commercial leases as well?",
    a: "Yes. We prepare and review commercial and retail shop leases, deal with the Retail Shop Leases Act disclosure requirements, negotiate assignments on business sales, and act on lease renewals and option exercises.",
  },
];

export const Route = createFileRoute("/commercial-conveyancing")({
  head: () => ({
    meta: [
      { title: fitTitle(TITLE) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      { property: "og:title", content: fitTitle(TITLE) },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://coastsidelaw.com.au/commercial-conveyancing" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/commercial-conveyancing" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LegalService",
          name: `${FIRM.name} — Commercial Conveyancing`,
          description: DESCRIPTION,
          telephone: FIRM.phone,
          areaServed: [
            { "@type": "State", name: "Queensland" },
            { "@type": "Place", name: "Sunshine Coast" },
            { "@type": "Place", name: "Moreton Bay" },
          ],
          address: {
            "@type": "PostalAddress",
            streetAddress: FIRM.street,
            addressLocality: FIRM.suburb,
            addressRegion: FIRM.state,
            postalCode: FIRM.postcode,
            addressCountry: FIRM.country,
          },
          serviceType: [
            "Commercial conveyancing",
            "Commercial property purchase",
            "Commercial property sale",
            "Commercial and retail leasing",
          ],
        }),
      },
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
    ],
  }),
  component: CommercialConveyancingPage,
});

const STAGES = [
  {
    title: "Before you sign",
    body: "The most valuable hour in a commercial deal. We read the contract, check the GST clause, confirm the permitted use, and tell you what the special conditions actually do. There is no cooling-off period on commercial property, so this is the only point at which changes are free.",
  },
  {
    title: "Due diligence",
    body: "Searches, leases, outgoings, planning, building compliance, contamination and body corporate records where relevant. We report on what we find in plain English, with a clear view on what matters and what does not.",
  },
  {
    title: "Conditions and negotiation",
    body: "Finance, due diligence and lease-review conditions have to be managed to their dates. We deal with the other side's solicitor, negotiate amendments and price adjustments where the searches justify them, and keep your broker in the loop.",
  },
  {
    title: "Settlement",
    body: "Adjustments for rates, water, land tax and outgoings, apportionment of rent and bonds, transfer of bank guarantees, notices to tenants, and electronic settlement. We confirm the outcome the same day.",
  },
];

const PROPERTY_TYPES = [
  "Offices and professional suites",
  "Retail shops and shopping centre tenancies",
  "Industrial sheds and warehouses",
  "Commercial and industrial strata units",
  "Medical and allied health premises",
  "Childcare, hospitality and licensed premises",
  "Development sites and vacant commercial land",
  "Freehold sold together with a business",
];

function CommercialConveyancingPage() {
  return (
    <>
      <PageHero
        personSlug="michael-klein"
        eyebrow="Commercial property"
        title="Commercial property conveyancing in Queensland"
        lead="Offices, retail, industrial and tenanted investments. No cooling-off period, real GST consequences and leases you inherit — commercial property rewards getting the contract right before you sign it."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <h2 className="font-serif text-2xl text-foreground">What commercial conveyancing involves</h2>
        <p className="mt-3 mb-8 text-base leading-relaxed text-muted-foreground">
          Commercial conveyancing is the legal work of buying or selling offices, shops, industrial
          premises and other commercial land. We review the contract, GST treatment, title,
          permitted use and any leases; manage the agreed conditions; and organise settlement.
          Your fixed fee is quoted after we see the deal, rather than applying a residential price.
          {" "}<Link to="/contact" className="font-semibold text-primary underline">Request a commercial property quote</Link>.
        </p>
        <div className="space-y-5 text-base leading-relaxed text-muted-foreground">
          <p>
            Commercial conveyancing is not residential conveyancing with a bigger number on it. The
            protections a home buyer takes for granted — a statutory cooling-off period, a standard
            REIQ contract everyone has seen a hundred times, a seller disclosure regime — either do
            not apply or apply differently. What you get instead is a negotiated contract, tenants
            whose leases you take over, and a GST position that can move the economics of the deal.
          </p>
          <p>
            Michael Klein has acted on commercial property and business transactions for more than
            twenty years, including as managing partner of a Moreton Bay firm. That work sits
            alongside a long litigation background, which is a useful thing in a commercial contract
            review: he has seen which clauses get argued about later.
          </p>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">How a commercial matter runs</h2>
          <div className="mt-8 space-y-6">
            {STAGES.map((s, i) => (
              <div key={s.title} className="rounded-sm border border-border bg-card p-6">
                <p className="text-xs font-semibold uppercase tracking-widest text-accent-foreground">
                  Step {i + 1}
                </p>
                <h3 className="mt-2 font-serif text-2xl text-foreground">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Commercial vs residential at a glance</h2>
          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border text-foreground">
                  <th className="py-2 pr-4"></th>
                  <th className="py-2 pr-4">Residential</th>
                  <th className="py-2">Commercial</th>
                </tr>
              </thead>
              <tbody className="text-muted-foreground">
                <tr className="border-b border-border/60"><td className="py-2 pr-4 font-semibold text-foreground">Cooling-off</td><td className="py-2 pr-4">5 business days</td><td className="py-2">None</td></tr>
                <tr className="border-b border-border/60"><td className="py-2 pr-4 font-semibold text-foreground">GST</td><td className="py-2 pr-4">Treatment depends on the property and sale</td><td className="py-2">Check liability, going-concern eligibility and any margin scheme calculation</td></tr>
                <tr className="border-b border-border/60"><td className="py-2 pr-4 font-semibold text-foreground">Contract</td><td className="py-2 pr-4">Standard REIQ form</td><td className="py-2">Often negotiated, with bespoke conditions</td></tr>
                <tr className="border-b border-border/60"><td className="py-2 pr-4 font-semibold text-foreground">Tenants</td><td className="py-2 pr-4">Residential tenancy, if any</td><td className="py-2">Leases, guarantees and outgoings you take over</td></tr>
                <tr><td className="py-2 pr-4 font-semibold text-foreground">Fee</td><td className="py-2 pr-4">Fixed: sale $990, purchase $1,499 incl. GST</td><td className="py-2">Fixed fee quoted after we see the deal</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Commercial conveyancing in Moreton Bay and the Sunshine Coast</h2>
          <div className="mt-5 space-y-5 text-base leading-relaxed text-muted-foreground">
            <p>
              We act from two offices: Scarborough on the Redcliffe Peninsula and Golden Beach near
              Caloundra. On the peninsula that is mostly Redcliffe and Margate shopfronts, strata
              offices, and Clontarf and Kippa-Ring industrial units. On the Sunshine Coast it is
              Caloundra and Kawana retail and Meridan Plains and Baringa light industrial and
              commercial strata.
            </p>
            <p>
              Settlement runs electronically wherever the property is, so we act on commercial
              property anywhere in Queensland. If a business is changing hands as well, see{" "}
              <Link to="/business-sales" className="font-semibold text-primary underline">business sales</Link>.
              If you are taking a lease rather than buying, see{" "}
              <Link to="/retail-shop-leases" className="font-semibold text-primary underline">retail shop leases</Link>{" "}
              (Form 12 certificate and lease review $880 incl. GST). Looking for a local office? See{" "}
              <Link to="/scarborough" className="font-semibold text-primary underline">solicitors Redcliffe and Scarborough</Link>.
            </p>
          </div>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Property we act on</h2>
          <ul className="mt-6 grid gap-2 sm:grid-cols-2">
            {PROPERTY_TYPES.map((t) => (
              <li
                key={t}
                className="rounded-sm border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
              >
                {t}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-14 rounded-sm border-l-2 border-accent bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">The three that cost the most</h2>
          <ul className="mt-4 space-y-3 text-sm leading-relaxed text-muted-foreground">
            <li>
              <strong className="text-foreground">GST.</strong> A contract silent on GST, or one that
              assumes going-concern treatment without meeting the conditions, can leave a buyer
              funding an unbudgeted 10% or a seller wearing it out of the price.
            </li>
            <li>
              <strong className="text-foreground">The leases.</strong> You buy the tenants as they
              are — including the rent review mechanism, the outgoings recovery, the make-good
              obligations and any option already exercised. A yield calculated off the rent roll
              means nothing if the leases do not support it.
            </li>
            <li>
              <strong className="text-foreground">Permitted use.</strong> A property being used a
              certain way is not proof that the use is lawfully approved. Confirm the approval before
              you commit, particularly for food, childcare, health and industrial uses.
            </li>
          </ul>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Buying the business too?</h2>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">
            Where the freehold and the business change hands together, the two contracts have to work
            with each other — settlement dates, the going-concern position and the restraint all need
            to line up.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/business-sales"
              className="rounded-sm border border-border px-5 py-2.5 text-sm font-semibold text-foreground card-elevate"
            >
              Buying or selling a business
            </Link>
            <Link
              to="/conveyancing"
              className="rounded-sm border border-border px-5 py-2.5 text-sm font-semibold text-foreground card-elevate"
            >
              Residential conveyancing fixed fees
            </Link>
          </div>
        </div>

        <PeopleStrip slugs={["michael-klein", "erin-wilkinson"]} />

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Commercial conveyancing FAQs</h2>
          <p className="mt-4 text-base text-muted-foreground">Read our <Link to="/guides/$slug" params={{ slug: "commercial-property-conveyancing-queensland" }} className="font-semibold text-primary underline">commercial property conveyancing guide</Link>{" "}or <Link to="/guides/$slug" params={{ slug: "due-diligence-clause-queensland" }} className="font-semibold text-primary underline">how due diligence conditions work</Link>.</p>
          <div className="mt-6">
            <FaqList items={FAQS} />
          </div>
        </div>

        <div className="mt-14">
          <h2 className="font-serif text-3xl text-foreground">Send us the contract</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Tell us briefly what the property is and where the deal is up to, and we will come back
            with a fixed fee and the issues we would want to look at first. Offices at {FIRM.street},{" "}
            {FIRM.suburb} and {PENINSULA_ADDRESS}.
          </p>
          <div className="mt-6">
            <EnquiryForm />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5">
        <ReviewStrip matterType="Commercial conveyancing" />
      </section>

      <CtaBand title="Commercial contract in front of you?" />
      <div className="h-20" />
    </>
  );
}
